import React, { useEffect, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { Paper, Box } from "@mui/material";

import { usePermissions } from "../../hooks/usePermissions";
import { useContentLanguage } from "../../hooks/useContentLanguage";
import { PageHeader } from "../../components/PageHeader";
import { LanguageTabs } from "../../components/LanguageTabs";

import { ItemsList } from "./ItemsList";
import { defaultInputValue } from "./ChallengeForm";
import { ChallengeForm } from "./ChallengeForm";
import { DeletionModal } from "./DeletionModal";
import { Loader } from "./Loader";
import { getItems } from "./getItems";
import { deleteItem } from "./deleteItem";

const RedeemCoins = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/redeem-coins");
  const [lang, setLang] = useContentLanguage();
  const [page, setPage] = useState(1);

  const [countPerPage, setCountPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [data, setData] = useState<any[]>([]);

  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [errors, setErrors] = useState(defaultInputValue);

  const [loadingData, setLoadingData] = useState(false);
  const [isEditing, setEditing] = useState(false);

  const [idForChallengeEdit, setIdForChallengeEdit] = useState("");
  const [idForChallengeDelete, setIdForChallengeDelete] = useState("");

  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [showDeletionModal, setDeletionModal] = useState(false);

  const [imageUploading, setImageUploading] = useState(false);

  const handleOnInputFieldChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleClose = () => {
    setDeletionModal(false);
    setFormModalOpen(false);
    setEditing(false);
    setErrors(defaultInputValue);
    setInputValue(defaultInputValue);
  };

  const handleGetNewsDetails = useCallback(() => {
    getItems({
      page,
      countPerPage,
      search,
      setLoadingData,
      setPage,
      setCountPerPage,
      setData,
      setCount,
    });
  }, [page, countPerPage, search]);

  useEffect(() => {
    handleGetNewsDetails();
  }, [handleGetNewsDetails]);

  const handleItemDeletionInModal = () =>
    deleteItem({
      idForChallengeDelete,
      page,
      countPerPage,
      search,
      setLoadingData,
      setPage,
      setCountPerPage,
      setData,
      setCount,
      setDeletionModal,
    });

  const onDeleteItem = (id: string) => {
    setIdForChallengeDelete(id);
    setDeletionModal(true);
  };

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Münzen einlösen"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={canCreate ? () => setFormModalOpen(true) : undefined}
          />

          <LanguageTabs value={lang} onChange={setLang} />

          {loadingData ? (
            <Loader />
          ) : (
            <ItemsList
              items={data}
              onDelete={onDeleteItem}
              canEdit={canEdit}
              canDelete={canDelete}
              lang={lang}
              onEdit={(item) => {
                setEditing(true);
                setFormModalOpen(true);
                setIdForChallengeEdit(item._id);
                setInputValue({
                  title: item.title,
                  title_ger: item.title_ger || "",
                  desc: item.desc,
                  desc_ger: item.desc_ger || "",
                  price: item.price,
                  image: item.image,
                  image_ger: item.image_ger || "",
                });
              }}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
            />
          )}
        </Box>
      </Paper>

      <DeletionModal
        showDeletionModal={showDeletionModal}
        setDeletionModal={setDeletionModal}
        handleClose={handleClose}
        handlerDeletion={handleItemDeletionInModal}
      />

      <ChallengeForm
        isOpen={formModalOpen}
        loading={imageUploading}
        isEditing={isEditing}
        editData={inputValue}
        idForChallengeEdit={idForChallengeEdit}
        onClose={handleClose}
        setEditing={setEditing}
        setLoading={setImageUploading}
        setModalOpen={setFormModalOpen}
        getNewsDetails={handleGetNewsDetails}
        handleOnInputFieldChange={handleOnInputFieldChange}
      />
    </>
  );
};

export default RedeemCoins;
